import { RoleBasedHandler } from '../../../../handlers/role-based-handler';
import { Component, effect, inject, OnInit } from '@angular/core';
import { ActiveUserStore } from '../../../../stores/user/active-user.state';
import { UserStore } from '../../../../stores/user/user.state';
import { PermissionStore } from '../../../../stores/permission.state';
import { CommuneStore } from '../../../../stores/commune/commune.state';
import { User } from '../../../../models/user';
import { ToolbarItem } from '../../../../models/toolbar-item';
import { SidebarService } from '../../../../services/menu/sidebar.service';
import { RoutingService } from '../../../../services/routing.service';
import { getState } from '@ngrx/signals';
import { HeaderComponent } from '../../../atoms/header/header.component';
import { ToolbarComponent } from '../../../organisms/toolbar/toolbar.component';
import { Button } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { SidebarModule } from 'primeng/sidebar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RouterLink, RouterOutlet } from '@angular/router';
import { NgIf } from '@angular/common';

interface DashboardCard {
  title: string;
  description: string;
  linkCaption: string;
  icon: string;
  routerLink?: any;
  cta?: () => void;
}

@Component({
  selector: 'app-authenticated-dashboard',
  standalone: true,
  imports: [
    HeaderComponent,
    ToolbarComponent,
    Button,
    DropdownModule,
    FormsModule,
    SidebarModule,
    ConfirmDialogModule,
    RouterOutlet,
    NgIf,
    RouterLink,
  ],
  templateUrl: './authenticated-dashboard.component.html',
  styleUrl: './authenticated-dashboard.component.scss',
})
export class AuthenticatedDashboardComponent implements OnInit {
  readonly roleBasedHandler = inject(RoleBasedHandler);
  readonly userStore = inject(ActiveUserStore);
  readonly usersStore = inject(UserStore);
  readonly permissionStore = inject(PermissionStore);
  readonly communeStore = inject(CommuneStore);

  protected storesInitialized = false;

  user!: User;
  cards: DashboardCard[] = [
    {
      title: 'Datenpflege',
      description: 'Pflege von Indikatoren, Räumen, Daten und Konfigurationen',
      routerLink: ['/data-backend'],
      linkCaption: 'zur Datenpflege',
      icon: 'fas fa-database',
    },
    {
      title: 'Interner Atlas',
      description: 'Interner Atlas mit geschützten Daten',
      routerLink: ['/atlas-intern'],
      linkCaption: 'zum Atlas',
      icon: 'fas fa-house-user',
    },
    {
      title: 'Öffentlicher Atlas',
      description: 'Atlas mit öffentlichen Daten',
      routerLink: ['/atlas/' + this.userStore.commune()],
      linkCaption: 'zum Atlas',
      icon: 'fas fa-earth-europe',
    },
    {
      title: 'Logout',
      description: 'Fertig, ausloggen!',
      linkCaption: 'ausloggen',
      cta: () => {
        this.logout();
      },
      icon: 'fas fa-right-from-bracket',
    },
  ];

  toolbarItems: ToolbarItem[] = [
    {
      icon: 'fas fa-city',
      key: 'commune',
      routerLink: ['/dashboard', { outlets: { overlay: 'kommunen' } }],
      label: `Kommunen`,
      tooltip: `Kommunen-Liste`,
    },
    {
      icon: 'fas fa-book',
      key: 'documentation',
      routerLink: ['/dashboard', { outlets: { overlay: 'dokumentation' } }],
      label: `Dokumentation`,
      tooltip: `Dokumentationseinträge verwalten`,
    },
    {
      icon: 'fas fa-bell',
      key: 'notification',
      routerLink: [
        '/dashboard',
        { outlets: { overlay: 'benachrichtigungen' } },
      ],
      label: `Dokumentation`,
      tooltip: `Benachrichtigungen verwalten`,
    },
  ];

  openSidebar = false;
  showToolbar = false;

  constructor(
    protected sidebarService: SidebarService,
    protected routingService: RoutingService
  ) {
    this.userStore.setContext('dataBackend');
    this.openSidebar = false;
    this.userStore.checkAuthStatus();
    effect(() => {
      const activeUserState = getState(this.userStore);
      const communeState = getState(this.communeStore);

      if (
        !this.storesInitialized &&
        !this.roleBasedHandler.areStoresInitialized()
      )
        return;
      this.storesInitialized = true;

      queueMicrotask(() => this.roleBasedHandler.performInitialization());
      if (this.permissionStore.isSuperAdmin()) {
        this.showToolbar = true;
      }
    });
  }

  /**
   * Initializes the component by retrieving the user data from the user store.
   * If the user data exists, it assigns it to the component's user property.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    const user = this.userStore.user();
    if (!user) return;
    this.user = user;
  }

  /**
   * Handles switching the commune for the current user.
   * This method queues a microtask to update the user's commune using the usersStore.
   * The update is performed asynchronously to ensure the operation executes after the current execution context.
   *
   * @return {void} Does not return a value.
   */
  switchCommune(): void {
    queueMicrotask(() => {
      this.usersStore.changeCommune(this.user);
      this.userStore.logout({ redirect: true });
    });
  }

  /**
   * Logs out the currently authenticated user and redirects them to the appropriate location.
   *
   * @return {void} Does not return a value.
   */
  logout(): void {
    this.userStore.logout({ redirect: true });
  }

  /**
   * Handles the close action for an overlay. This method is triggered when the overlay is closed.
   *
   * @return {void} Does not return a value.
   */
  onOverlayClose(): void {
    console.log('closed');
  }
}
